'use client';

import { useState, useCallback, useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';

// ========================================
// Constants
// ========================================
const PROMO_VIEWED_KEY = 'newsletter_promo_viewed';
const PROMO_DISMISSED_KEY = 'promo_tab_dismissed_at';
const SALE_CACHE_KEY = 'sale_announcement_data';
const SALE_DISMISS_KEY = 'sale_announcement_dismissed';
const PROMO_MODAL_DELAY_MS = 12000;
const SIDE_TAB_INITIAL_DELAY_MS = 15000; // delay before any side tab may appear on a fresh page load
const PROMO_DISMISS_DURATION_MS = 90 * 60 * 1000; // 90 minutes
const SALE_CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour
const SALE_DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * useSideTabs - Manages promo modal and side tab state
 *
 * Side tabs appear on the left edge of the screen to promote newsletter signup
 * and active sales. This hook manages:
 *
 * 1. Promo Modal Flow:
 *    - First visit: modal appears after 4s delay
 *    - User can dismiss (X/background), refuse ("No thanks"), or complete signup
 *    - All dismissal methods suppress tabs until next route change
 *
 * 2. Promo Tab:
 *    - Shows after modal is dismissed (if not explicitly refused)
 *    - Dismissal expires after 90 minutes
 *
 * 3. Sale Tab:
 *    - Shows when promo is completed/dismissed AND active sales exist
 *    - Dismissal expires after 24 hours
 *    - Cached for 1 hour to avoid repeated API calls
 *
 * 4. Route-based suppression:
 *    - When user dismisses any tab/modal, ALL tabs are hidden until next navigation
 *    - Prevents jarring experience of one tab replacing another
 *
 * 5. Route restrictions:
 *    - No tabs on /cart or /checkout routes
 *
 * 6. Initial-load delay:
 *    - No side tab may appear until 15s after a fresh page load
 */
const useSideTabs = () => {
  const pathname = usePathname();
  const isCartOrCheckout = pathname?.startsWith('/cart') || pathname?.startsWith('/checkout');

  // ========================================
  // State
  // ========================================
  // Promo states: 'loading' | 'modal_pending' | 'modal_open' | 'tab_showing' | 'tab_dismissed' | 'completed'
  const [promoState, setPromoState] = useState('loading');
  // Sale states: 'loading' | 'unavailable' | 'ready' | 'dismissed'
  const [saleState, setSaleState] = useState('loading');
  const [saleData, setSaleData] = useState(null);

  // Route-based suppression: prevents tabs from appearing until next navigation
  const [promoSuppressedThisRoute, setPromoSuppressedThisRoute] = useState(false);
  const [saleSuppressedThisRoute, setSaleSuppressedThisRoute] = useState(false);

  // Initial-load suppression: no side tab may appear until 15s after a fresh
  // page load (returning visitors otherwise get a tab instantly on mount)
  const [initialDelayElapsed, setInitialDelayElapsed] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setInitialDelayElapsed(true), SIDE_TAB_INITIAL_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  // ========================================
  // Clear route-based suppression on navigation
  // ========================================
  useEffect(() => {
    setPromoSuppressedThisRoute(false);
    setSaleSuppressedThisRoute(false);
  }, [pathname]);

  // ========================================
  // Promo Modal Actions
  // ========================================

  // Open promo modal (e.g., from clicking the promo tab)
  const openPromoModal = useCallback(() => {
    // Mark as viewed so it won't auto-pop again (but tab can still open it)
    localStorage.setItem(PROMO_VIEWED_KEY, 'true');
    setPromoState('modal_open');
  }, []);

  // Close modal via background click or X button
  // User may still want to see it later, so tab will show on next route
  const hidePromoModal = useCallback(() => {
    // Suppress all tabs until route change
    setPromoSuppressedThisRoute(true);
    setSaleSuppressedThisRoute(true);
    setPromoState('tab_showing');
  }, []);

  // Close modal via "No thanks" - explicit refusal
  // User doesn't want promo, dismiss for 90 minutes
  const refusePromoModal = useCallback(() => {
    localStorage.setItem(PROMO_VIEWED_KEY, 'true');
    localStorage.setItem(PROMO_DISMISSED_KEY, Date.now().toString());
    // Suppress all tabs until route change
    setSaleSuppressedThisRoute(true);
    setPromoState('tab_dismissed');
  }, []);

  // User successfully signed up via modal
  // Don't suppress sale tab - successful signup is a positive interaction
  const completePromoSignup = useCallback(() => {
    localStorage.setItem(PROMO_VIEWED_KEY, 'true');
    localStorage.setItem(PROMO_DISMISSED_KEY, Date.now().toString());
    setPromoState('completed');
  }, []);

  // ========================================
  // Promo Tab Actions
  // ========================================

  // Dismiss the promo tab (X button on tab itself)
  const dismissPromoTab = useCallback(() => {
    localStorage.setItem(PROMO_DISMISSED_KEY, Date.now().toString());
    setPromoState('tab_dismissed');
    // Suppress sale tab until route change
    setSaleSuppressedThisRoute(true);
  }, []);

  // ========================================
  // Sale Tab Actions
  // ========================================

  // Dismiss the sale tab
  const dismissSaleTab = useCallback(() => {
    const dismissData = {
      dismissed: true,
      expiresAt: Date.now() + SALE_DISMISS_DURATION_MS
    };
    localStorage.setItem(SALE_DISMISS_KEY, JSON.stringify(dismissData));
    setSaleState('dismissed');
  }, []);

  // ========================================
  // Derived: What side tab should show
  // ========================================
  const sideTabToShow = useMemo(() => {
    // Fresh page load - no tab for the first 15 seconds
    if (!initialDelayElapsed) {
      return 'none';
    }
    // Modal is pending or open - hide all tabs
    if ((promoState === 'modal_pending' || promoState === 'modal_open') && !isCartOrCheckout) {
      return 'none';
    }
    // Promo tab (not on cart/checkout, not suppressed this route)
    if (promoState === 'tab_showing' && !isCartOrCheckout && !promoSuppressedThisRoute) {
      return 'promo';
    }
    // Sale tab (not on cart/checkout, not suppressed this route, has sales)
    if (saleState === 'ready' && !isCartOrCheckout && !saleSuppressedThisRoute) {
      return 'sale';
    }
    return 'none';
  }, [promoState, saleState, isCartOrCheckout, promoSuppressedThisRoute, saleSuppressedThisRoute, initialDelayElapsed]);

  // Promo modal visibility
  const promoModalOpen = promoState === 'modal_open';
  // Should actually show the modal (not on cart/checkout)
  const shouldShowPromoModal = promoModalOpen && !isCartOrCheckout;

  // ========================================
  // Initialization (runs once on mount)
  // ========================================
  useEffect(() => {
    // --- Initialize Promo State ---
    const hasViewedPromo = localStorage.getItem(PROMO_VIEWED_KEY) === 'true';
    const dismissedAt = localStorage.getItem(PROMO_DISMISSED_KEY);
    const promoDismissedRecently = dismissedAt && Date.now() - parseInt(dismissedAt, 10) < PROMO_DISMISS_DURATION_MS;
    if (!hasViewedPromo) {
      // First visit - hide all tabs while modal is pending, then show modal
      setPromoState('modal_pending');
      const timer_0 = setTimeout(() => {
        localStorage.setItem(PROMO_VIEWED_KEY, 'true');
        setPromoState('modal_open');
      }, PROMO_MODAL_DELAY_MS);
      // Note: We don't clean up this timer - it's a one-time initialization
      // and cleaning it up would prevent the modal from ever showing
    } else if (promoDismissedRecently) {
      // Promo was dismissed within 90 minutes - stay dismissed
      setPromoState('tab_dismissed');
    } else {
      // Promo was seen but dismissal expired - show tab again
      setPromoState('tab_showing');
    }

    // --- Initialize Sale State ---
    const initSaleState = async () => {
      // Check if sale was dismissed
      const dismissedData = localStorage.getItem(SALE_DISMISS_KEY);
      if (dismissedData) {
        try {
          const parsed = JSON.parse(dismissedData);
          if (parsed.dismissed && parsed.expiresAt > Date.now()) {
            setSaleState('dismissed');
            return;
          }
          localStorage.removeItem(SALE_DISMISS_KEY);
        } catch {
          localStorage.removeItem(SALE_DISMISS_KEY);
        }
      }

      // Check cached sale data
      const cachedData = localStorage.getItem(SALE_CACHE_KEY);
      if (cachedData) {
        try {
          const parsed_0 = JSON.parse(cachedData);
          if (parsed_0.expiresAt > Date.now()) {
            setSaleData(parsed_0.data);
            setSaleState(parsed_0.data?.hasSales ? 'ready' : 'unavailable');
            return;
          }
        } catch {
          localStorage.removeItem(SALE_CACHE_KEY);
        }
      }

      // Fetch fresh sale data
      try {
        const response = await fetch('/api/sale/announcement');
        const data = await response.json();
        const cacheEntry = {
          data,
          expiresAt: Date.now() + SALE_CACHE_DURATION_MS
        };
        localStorage.setItem(SALE_CACHE_KEY, JSON.stringify(cacheEntry));
        setSaleData(data);
        setSaleState(data?.hasSales ? 'ready' : 'unavailable');
      } catch (error) {
        console.error('Failed to fetch sale data:', error);
        setSaleState('unavailable');
      }
    };
    initSaleState();
  }, []);
  return {
    // Which tab to show (or 'none')
    sideTabToShow,
    // Sale data for SaleSideTab
    saleData,
    // Promo modal state (shouldShowPromoModal accounts for cart/checkout routes)
    shouldShowPromoModal,
    // Promo modal actions
    openPromoModal,
    hidePromoModal,
    refusePromoModal,
    completePromoSignup,
    // Promo tab actions
    dismissPromoTab,
    // Sale tab actions
    dismissSaleTab
  };
};
export default useSideTabs;